import React from "react";
import { Button } from "reactstrap";
import { SHORT_LINK_ERROR_KINDS } from "../../../../utils/trivvyShortLink";
import { SHORT_LINK_STATUS } from "../../../../hooks/useTrivvyShortLink";
import {
  SHARE_LINK_LOADING,
  SHARE_LINK_RETRY,
  SHARE_LINK_LOAD_FAILED,
  SHARE_LINK_USE_ORIGINAL,
  SHARE_LINK_FALLBACK_NOTE,
  SHARE_LINK_SESSION_EXPIRED,
  SHARE_LINK_PERMISSION_DENIED,
  SHARE_LINK_UNAVAILABLE,
  SHARE_LINK_RATE_LIMITED,
  SHARE_LINK_CONFIG_ERROR,
} from "./shareCopy";

// Message per failure, and whether Retry makes sense for it. 401/403/404 and
// config errors are not transient, so they get neither Retry nor a fallback.
const LINK_ERROR_UI = {
  [SHORT_LINK_ERROR_KINDS.UNAUTHORIZED]: { message: SHARE_LINK_SESSION_EXPIRED, canRetry: false },
  [SHORT_LINK_ERROR_KINDS.FORBIDDEN]: { message: SHARE_LINK_PERMISSION_DENIED, canRetry: false },
  [SHORT_LINK_ERROR_KINDS.NOT_FOUND]: { message: SHARE_LINK_UNAVAILABLE, canRetry: false },
  [SHORT_LINK_ERROR_KINDS.INVALID_RESPONSE]: { message: SHARE_LINK_CONFIG_ERROR, canRetry: false },
  [SHORT_LINK_ERROR_KINDS.RATE_LIMITED]: { message: SHARE_LINK_RATE_LIMITED, canRetry: true },
  [SHORT_LINK_ERROR_KINDS.BAD_REQUEST]: { message: SHARE_LINK_LOAD_FAILED, canRetry: true },
  [SHORT_LINK_ERROR_KINDS.CONFLICT]: { message: SHARE_LINK_LOAD_FAILED, canRetry: true },
  [SHORT_LINK_ERROR_KINDS.SERVER]: { message: SHARE_LINK_LOAD_FAILED, canRetry: true },
  [SHORT_LINK_ERROR_KINDS.NETWORK]: { message: SHARE_LINK_LOAD_FAILED, canRetry: true },
};

// TBV-938: the status line under any short-link surface (Copy, QR, WhatsApp):
// loading text, a safe per-error message, Retry, and the explicit
// "Use original link" choice — offered only for transient failures and only
// when the surface has a validated original link.
const ShortLinkStatus = ({
  shortLink,
  hasFallback,
  isUsingFallback,
  onUseFallback,
  loadingText = SHARE_LINK_LOADING,
  className = "",
}) => {
  const { linkStatus, linkErrorKind, isRetryBlocked, canOfferFallback, retry } = shortLink;
  const errorUi =
    linkStatus === SHORT_LINK_STATUS.ERROR ? LINK_ERROR_UI[linkErrorKind] || LINK_ERROR_UI.SERVER : null;

  let statusMessage = "";
  if (linkStatus === SHORT_LINK_STATUS.LOADING) {
    statusMessage = loadingText;
  } else if (isUsingFallback) {
    statusMessage = SHARE_LINK_FALLBACK_NOTE;
  } else if (errorUi) {
    statusMessage = errorUi.message;
  }

  return (
    <div className={`d-flex align-items-center flex-wrap ${className}`} aria-live="polite">
      {statusMessage && <small className="text-muted mr-2">{statusMessage}</small>}
      {errorUi && errorUi.canRetry && (
        <Button
          color="link"
          size="sm"
          className="p-0 mr-3"
          disabled={isRetryBlocked}
          onClick={retry}
        >
          {SHARE_LINK_RETRY}
        </Button>
      )}
      {canOfferFallback && hasFallback && !isUsingFallback && (
        <Button color="link" size="sm" className="p-0" onClick={onUseFallback}>
          {SHARE_LINK_USE_ORIGINAL}
        </Button>
      )}
    </div>
  );
};

export default ShortLinkStatus;
